/**
 * A CÉG ŰRLAPJA — felvitel és szerkesztés, EGY komponensben.
 *
 * ⚠️ EGY, ÉS NEM KETTŐ, mert a mezők és a szabályaik azonosak — a backenden
 * is (`create-company.dto.ts` és `update-company.dto.ts` ugyanazokat a
 * korlátokat viszi). Két komponens itt azt a hibát hozná be, amire a
 * `update-company.dto.ts` fejléce figyelmeztet: egy ÚJ MEZŐ az egyikben
 * megjelenik, a másikban nem, és a hiány csendes.
 *
 * ═══ AMI SZÁNDÉKOSAN NINCS RAJTA: AZ `active` ═══
 *
 * Az inaktiválás DÖNTÉS, nem adatbevitel, és saját útja van
 * (`PUT /companies/:id/active`). Egy űrlapmezőként egy régóta nyitva
 * felejtett lap elavult adattal visszaküldve kikapcsolhatna egy céget, amit
 * közben valaki visszakapcsolt — és semmi nem mondaná meg, hogy ez történt.
 *
 * ═══ A KÉT NULLÁZHATÓ MEZŐ ═══
 *
 * Az üres `groupMemberTaxNumber` és `fiscalYearStartMonth` `null`-ként megy
 * ki, nem üres szövegként, és ez MŰVELET: „a csoportos adóalanyiság
 * megszűnt", illetve „naptári év". A backend a `null`-t kimondottan
 * átengedi ezen a két mezőn, és CSAK ezen a kettőn — a többin 400-at ad.
 */

import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CompaniesState, CompaniesStatus, CompanyInput, CompanyRow } from './useCompanies';

/**
 * A CÉG ALAPÉRTELMEZÉSEI — ugyanazok, amiket a séma ad (`schema.prisma`).
 *
 * ⚠️ ITT MEGISMÉTELVE, ÉS EZ TUDATOS DUPLIKÁCIÓ. A felvitelnél a mezők nem
 * állhatnak üresen: egy üres „megőrzési idő" mellett a könyvelő azt hinné,
 * nincs ilyen szabály — holott nyolc év a törvényi alap. A szerver ugyanezt
 * teszi oda, ha a mező hiányzik, tehát a két érték egyezik; ha egyszer
 * elcsúsznának, a szerveré nyer, és a felület hazudott volna.
 */
const DEFAULTS = { retentionYears: 8, chaseGraceDays: 5, locale: 'hu' } as const;

export interface CompanyFormProps {
  /** `null` felvitelnél. */
  company: CompanyRow | null;
  state: CompaniesState;
  /**
   * A szelet zárója — mentés után és „Mégse"-re. A hibát is törli
   * (`clearWriteError`), és az `Escape` is ezt hívja (`useSettingsLayer`).
   */
  onDone: () => void;
}

export function CompanyForm({ company, state, onDone }: CompanyFormProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(company?.name ?? '');
  const [taxNumber, setTaxNumber] = useState(company?.taxNumber ?? '');
  const [groupMemberTaxNumber, setGroup] = useState(company?.groupMemberTaxNumber ?? '');
  const [retentionYears, setRetention] = useState(
    String(company?.retentionYears ?? DEFAULTS.retentionYears)
  );
  const [chaseGraceDays, setGrace] = useState(
    String(company?.chaseGraceDays ?? DEFAULTS.chaseGraceDays)
  );
  const [fiscalYearStartMonth, setFiscal] = useState(
    company?.fiscalYearStartMonth === null || company?.fiscalYearStartMonth === undefined
      ? ''
      : String(company.fiscalYearStartMonth)
  );
  const [locale, setLocale] = useState(company?.locale ?? DEFAULTS.locale);
  const [tensoftCode, setTensoftCode] = useState(company?.tensoftCode ?? '');

  /**
   * A horog állapota MEGOSZTOTT a lista és az űrlap között: egy korábbi
   * sorművelet hibája nem jelenhet meg egy frissen megnyitott űrlapon. A
   * zászló azt mondja meg, hogy a MI beküldésünk volt az.
   *
   * ⚠️ A BEZÁRÁS NEM INNEN JÖN, hanem a horog `onSaved` visszahívásából — a
   * `saved` állapotot az űrlap soha nem látja (`useCompanies`, `write`).
   */
  const [submitted, setSubmitted] = useState(false);
  const busy = submitted && state.status === 'saving';

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    const input = toInput({
      name,
      taxNumber,
      groupMemberTaxNumber,
      retentionYears,
      chaseGraceDays,
      fiscalYearStartMonth,
      locale,
      tensoftCode,
    });

    setSubmitted(true);
    if (company) {
      state.update(company.id, input, onDone);
    } else {
      state.create(input, onDone);
    }
  }

  return (
    <form className="card settings__form" onSubmit={onSubmit}>
      <h2>{company ? t('companies.form.editTitle') : t('companies.form.createTitle')}</h2>

      <FormError status={submitted ? state.status : null} detail={state.detail} />

      <label htmlFor="company-name">{t('companies.form.name')}</label>
      <input
        id="company-name"
        required
        // A billentyűzet-első felület a fókusszal kezd, nem az egérrel.
        // biome-ignore lint/a11y/noAutofocus: az űrlap a képernyő egyetlen tartalma
        autoFocus
        value={name}
        onChange={(event) => setName(event.target.value)}
      />

      <label htmlFor="company-tax">{t('companies.form.taxNumber')}</label>
      <input
        id="company-tax"
        required
        inputMode="numeric"
        aria-describedby="company-tax-hint"
        value={taxNumber}
        onChange={(event) => setTaxNumber(event.target.value)}
      />
      <p id="company-tax-hint" className="hint">
        {t('companies.form.taxNumberHint')}
      </p>

      <label htmlFor="company-group">{t('companies.form.groupMemberTaxNumber')}</label>
      <input
        id="company-group"
        inputMode="numeric"
        aria-describedby="company-group-hint"
        value={groupMemberTaxNumber}
        onChange={(event) => setGroup(event.target.value)}
      />
      <p id="company-group-hint" className="hint">
        {t('companies.form.groupMemberHint')}
      </p>

      <label htmlFor="company-retention">{t('companies.form.retentionYears')}</label>
      <input
        id="company-retention"
        type="number"
        min={1}
        max={30}
        required
        aria-describedby="company-retention-hint"
        value={retentionYears}
        onChange={(event) => setRetention(event.target.value)}
      />
      <p id="company-retention-hint" className="hint">
        {t('companies.form.retentionHint')}
      </p>

      <label htmlFor="company-grace">{t('companies.form.chaseGraceDays')}</label>
      <input
        id="company-grace"
        type="number"
        min={0}
        max={90}
        required
        aria-describedby="company-grace-hint"
        value={chaseGraceDays}
        onChange={(event) => setGrace(event.target.value)}
      />
      <p id="company-grace-hint" className="hint">
        {t('companies.form.chaseGraceHint')}
      </p>

      <label htmlFor="company-fiscal">{t('companies.form.fiscalYearStartMonth')}</label>
      <input
        id="company-fiscal"
        type="number"
        min={1}
        max={12}
        aria-describedby="company-fiscal-hint"
        value={fiscalYearStartMonth}
        onChange={(event) => setFiscal(event.target.value)}
      />
      <p id="company-fiscal-hint" className="hint">
        {t('companies.form.fiscalYearHint')}
      </p>

      <label htmlFor="company-locale">{t('companies.form.locale')}</label>
      <input
        id="company-locale"
        aria-describedby="company-locale-hint"
        value={locale}
        onChange={(event) => setLocale(event.target.value)}
      />
      <p id="company-locale-hint" className="hint">
        {t('companies.form.localeHint')}
      </p>

      <label htmlFor="company-tensoft-code">{t('companies.form.tensoftCode')}</label>
      <input
        id="company-tensoft-code"
        maxLength={2}
        pattern="[A-Za-z0-9]{2}"
        autoComplete="off"
        aria-describedby="company-tensoft-code-hint"
        value={tensoftCode}
        onChange={(event) => setTensoftCode(event.target.value.toUpperCase())}
      />
      <p id="company-tensoft-code-hint" className="hint">
        {t('companies.form.tensoftCodeHint')}
      </p>

      <div className="settings__toolbar">
        <button type="submit" className="settings__primary" disabled={busy}>
          {busy ? t('companies.form.saving') : t('companies.form.save')}
        </button>
        <button type="button" onClick={onDone} disabled={busy}>
          {t('companies.form.cancel')}
        </button>
      </div>
    </form>
  );
}

/**
 * AZ ŰRLAP MEZŐI → A KÉRÉS TÖRZSE. Tiszta függvény, a komponensen kívül.
 *
 * ⚠️ ÜRES MEZŐ = `null`, NEM `''`, és CSAK a három nullázható mezőn. A `''` a
 * backend `@Matches` mintáján bukna el („az adószám alakja…"), ami
 * félrevezető hibaüzenet arra, hogy a mezőt egyszerűen nem töltötték ki. A
 * `null` ezen a hármon MŰVELET: „a csoportos adóalanyiság megszűnt",
 * „naptári év", illetve a Tensoft-kódnál „nem ismerjük" — a többi mezőn a szerver 400-zal utasítja el
 * (`update-company.dto.ts`).
 */
export function toInput(fields: {
  name: string;
  taxNumber: string;
  groupMemberTaxNumber: string;
  retentionYears: string;
  chaseGraceDays: string;
  fiscalYearStartMonth: string;
  locale: string;
  tensoftCode: string;
}): CompanyInput {
  return {
    name: fields.name.trim(),
    taxNumber: fields.taxNumber.trim(),
    groupMemberTaxNumber:
      fields.groupMemberTaxNumber.trim() === '' ? null : fields.groupMemberTaxNumber.trim(),
    retentionYears: Number(fields.retentionYears),
    chaseGraceDays: Number(fields.chaseGraceDays),
    fiscalYearStartMonth:
      fields.fiscalYearStartMonth === '' ? null : Number(fields.fiscalYearStartMonth),
    locale: fields.locale,
    tensoftCode: fields.tensoftCode.trim() === '' ? null : fields.tensoftCode.trim(),
  };
}

/**
 * A MENTÉS HIBÁJA — és MELYIK üzenettel.
 *
 * ⚠️ A `conflict` ÁG A SZERVER SZÖVEGÉT MUTATJA, SZÓ SZERINT. Az üzenet
 * megnevezi az ütköző céget, és megmondja, ha az INAKTÍV — vagyis a teendő
 * nem új felvitel, hanem visszakapcsolás. Egy saját „már létezik" szöveg ezt
 * elnyelné, és a könyvelő újra meg újra próbálkozna.
 */
function FormError({ status, detail }: { status: CompaniesStatus | null; detail: string }) {
  const { t } = useTranslation();

  if (status === 'conflict') {
    return (
      <p className="error" role="alert">
        {detail}
      </p>
    );
  }
  if (status === 'invalid') {
    return (
      <p className="error" role="alert">
        {t('companies.form.invalid', { detail })}
      </p>
    );
  }
  if (status === 'failed') {
    return (
      <p className="error" role="alert">
        {t('companies.form.failed')}
      </p>
    );
  }
  return null;
}
